import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _ACTION_HANDLERS;
var _excluded = ["onSubmit", "values", "rules", "formNotReady"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import _property from 'lodash/property';
import _curry from 'lodash/curry';
import _get from 'lodash/get';

// Utils
import selectorWithoutMemoize from '@tekion/tekion-base/utils/selectorWithoutMemoize';

// Constants
import { EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';
import FORM_ACTION_TYPES from '../../organisms/FormBuilder/constants/actionTypes';
import FORM_PAGE_ACTION_TYPES from '../../pages/formPage/constants/actionTypes';

// Helpers
import { defaultOnChangeHandler } from './withFormPageState.helpers';
import { getTableDataWithLastRowBoolean } from '../../molecules/tableInputField/utils/tableStateHelpers';
export var getFieldId = _property('params.id');
var getSetState = _property('setState');
var getGetState = _property('getState');
var getErrors = _property('params.errors');
var setErrorsInState = function setErrorsInState(setState, errors, getState) {
  var _getState = getState(),
    notifyErrors = _getState.notifyErrors;
  setState({
    errors: errors
  });
  notifyErrors(errors);
};
var handleError = selectorWithoutMemoize([getSetState, getErrors, getGetState], setErrorsInState);

/**
 * Handles the change of a field by calling the specific onChange handler for the field.
 * If the onChange handler does not exist for the field then calls the default onchange handler.
 * @param {Object} args: Contain the getState and setState methods, action payload as params and a map of fieldId vs onChangeHandler.
 */
var handleChange = function handleChange(_ref) {
  var getState = _ref.getState,
    setState = _ref.setState,
    _ref$params = _ref.params,
    params = _ref$params === void 0 ? EMPTY_OBJECT : _ref$params,
    mapFieldToOnChange = _ref.mapFieldToOnChange;
  var fieldId = params.id;
  var onChange = mapFieldToOnChange[fieldId] || defaultOnChangeHandler;
  onChange({
    setState: setState,
    getState: getState,
    params: params
  });
};

/**
 * Adds a new row to the table field by pushing a new object in the table data array.
 * @param {String} fieldId: Id of the table field in which we want to add the new array.
 * @param {Function} setState: set State method to set the new value state.
 * @param {Object} prevState: State just before the execution of this particular setState.
 */
export var addNewRowToTable = _curry(function (fieldId, setState, newRowDefaultData, prevState) {
  var currentTableValue = _get(prevState, "values.".concat(fieldId));
  var newTableValue = getTableDataWithLastRowBoolean([].concat(_toConsumableArray(currentTableValue), [newRowDefaultData]));
  setState({
    values: _objectSpread(_objectSpread({}, prevState.values), {}, _defineProperty({}, fieldId, newTableValue))
  });
});

/**
 * Handles the action thrown by the table input field to add a new row to the editable table.
 * @param {Object} params: Contains action object, getState method and setState method.
 */
var handleAddNewRow = function handleAddNewRow() {
  var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var fieldId = getFieldId(params);
  var _params$getState = params.getState(),
    _params$getState$newR = _params$getState.newRowDefaultData,
    newRowDefaultData = _params$getState$newR === void 0 ? EMPTY_OBJECT : _params$getState$newR;
  params.setState(addNewRowToTable(fieldId, params.setState, newRowDefaultData));
};
var notifyFormNotReady = function notifyFormNotReady(_ref2) {
  var getState = _ref2.getState;
  var _getState2 = getState(),
    onAction = _getState2.onAction;
  onAction({
    type: FORM_ACTION_TYPES.NOTIFY_NOT_READY
  });
};
var handleSubmit = function handleSubmit(_ref3) {
  var getState = _ref3.getState,
    _ref3$params = _ref3.params,
    params = _ref3$params === void 0 ? EMPTY_OBJECT : _ref3$params;
  var _getState3 = getState(),
    onSubmit = _getState3.onSubmit,
    values = _getState3.values,
    rules = _getState3.rules,
    formNotReady = _getState3.formNotReady,
    restState = _objectWithoutProperties(_getState3, _excluded);
  var additional = params.additional;
  if (formNotReady) {
    notifyFormNotReady({
      getState: getState
    });
    return;
  }
  onSubmit(values, restState, additional);
};
var handleTertiaryAction = function handleTertiaryAction(_ref4) {
  var getState = _ref4.getState;
  var _getState4 = getState(),
    onTertiaryAction = _getState4.onTertiaryAction,
    values = _getState4.values,
    formNotReady = _getState4.formNotReady;
  if (formNotReady) {
    notifyFormNotReady({
      getState: getState
    });
    return;
  }
  onTertiaryAction(values);
};
var handleRuleChange = function handleRuleChange(_ref5) {
  var setState = _ref5.setState,
    _ref5$params = _ref5.params,
    params = _ref5$params === void 0 ? EMPTY_OBJECT : _ref5$params;
  var rules = params.rules;
  setState({
    rules: rules
  });
};
var ACTION_HANDLERS = (_ACTION_HANDLERS = {}, _defineProperty(_ACTION_HANDLERS, FORM_ACTION_TYPES.ON_FIELD_CHANGE, handleChange), _defineProperty(_ACTION_HANDLERS, FORM_ACTION_TYPES.VALIDATION_SUCCESS, handleError), _defineProperty(_ACTION_HANDLERS, "TABLE_ADD_NEW_ROW", handleAddNewRow), _defineProperty(_ACTION_HANDLERS, FORM_PAGE_ACTION_TYPES.ON_FORM_SUBMIT, handleSubmit), _defineProperty(_ACTION_HANDLERS, FORM_PAGE_ACTION_TYPES.ON_TERTIARY_ACTION, handleTertiaryAction), _defineProperty(_ACTION_HANDLERS, FORM_ACTION_TYPES.ON_RULE_CHANGE, handleRuleChange), _ACTION_HANDLERS);
export default ACTION_HANDLERS;